import type { LegalDoc } from "../types";
import { LEGAL_COMPANY as C } from "../company";

export const platformRules: LegalDoc = {
  slug: "platform-rules",
  title: "Quy chế hoạt động của nền tảng",
  metaTitle: "Quy chế hoạt động",
  metaDescription:
    "Quy chế hoạt động của nền tảng Chốt Ca: phân định trách nhiệm giữa Chốt Ca, Nhà tuyển dụng và Người tìm việc, quy trình vận hành và giải quyết khiếu nại.",
  version: "1.0.0",
  effectiveDate: C.effectiveDate,
  intro: `Quy chế này được ${C.legalName} xây dựng và công bố công khai theo **Điều 39 Luật Bảo vệ quyền lợi người tiêu dùng số 19/2023/QH15**. Mục đích của Quy chế là **phân định rõ trách nhiệm của từng bên** tham gia trên nền tảng Chốt Ca, và mô tả các quy trình vận hành để bạn biết mình được bảo vệ tới đâu và phải tự chịu trách nhiệm ở đâu.\n\nQuy chế này áp dụng cùng với [Điều khoản sử dụng](/terms). Khi có mâu thuẫn về nội dung phân định trách nhiệm, Quy chế này được ưu tiên.`,
  sections: [
    {
      title: "1. Chủ thể vận hành và đầu mối liên hệ",
      body: `**Đơn vị vận hành**: ${C.legalName}${C.taxId ? `, mã số doanh nghiệp ${C.taxId}` : ""}\n**Địa chỉ trụ sở**: ${C.address}\n**Người đại diện theo pháp luật**: ${C.representative}\n\nCác đầu mối được chỉ định và công bố công khai theo Điều 39 khoản 3 điểm a và điểm e Luật Bảo vệ quyền lợi người tiêu dùng 2023:`,
      items: [
        `**Đầu mối tiếp nhận phản ánh, khiếu nại của người dùng**: ${C.email}`,
        `**Đầu mối tiếp nhận báo cáo nội dung vi phạm**: ${C.abuseEmail}`,
        `**Đầu mối về bảo vệ dữ liệu cá nhân**: ${C.dpoEmail}`,
        `**Đầu mối về thanh toán, hoá đơn, đối soát giao dịch**: ${C.billingEmail}`,
        `**Đầu mối phối hợp với cơ quan nhà nước có thẩm quyền và giải quyết khiếu nại quyết định xử lý**: ${C.legalEmail}`,
      ],
    },
    {
      title: "2. Các bên tham gia",
      items: [
        "**Chốt Ca** — bên thiết lập và vận hành nền tảng, cung cấp công cụ kết nối và thu phí dịch vụ nền tảng từ Nhà tuyển dụng.",
        "**Nhà tuyển dụng** — bên đăng tin tuyển dụng, tiếp nhận hồ sơ, quyết định tuyển dụng và giao kết hợp đồng lao động. Đồng thời là khách hàng trả phí của Chốt Ca.",
        "**Người tìm việc** — cá nhân tạo hồ sơ, ứng tuyển và nhận lời mời phỏng vấn. **Không trả bất kỳ khoản phí nào** cho Chốt Ca.",
      ],
    },
    {
      title: "3. Phân định trách nhiệm giữa các bên",
      sections: [
        {
          title: "3.1. Chốt Ca chịu trách nhiệm",
          items: [
            "Vận hành nền tảng ổn định trong khả năng hợp lý, và khắc phục sự cố kỹ thuật do phía mình gây ra.",
            "Hiển thị đầy đủ, chính xác thông tin về gói dịch vụ, giá và hạn mức trước khi Nhà tuyển dụng xác nhận thanh toán.",
            "Bảo mật dữ liệu cá nhân theo [Chính sách bảo vệ dữ liệu cá nhân](/privacy) và chỉ mở thông tin liên hệ của Người tìm việc theo đúng các mốc đã công bố.",
            "Tiếp nhận, xem xét và xử lý báo cáo nội dung vi phạm trong **24 giờ**.",
            "Cung cấp cho Nhà tuyển dụng khả năng tra cứu, tải về và in hoá đơn, chứng từ giao dịch của mình.",
            "Xác thực doanh nghiệp theo hồ sơ tự nguyện nộp, và hiển thị trung thực trạng thái xác minh đó.",
            "Phối hợp với cơ quan nhà nước có thẩm quyền khi có yêu cầu hợp pháp.",
          ],
        },
        {
          title: "3.2. Chốt Ca KHÔNG chịu trách nhiệm",
          body: "Đây là ranh giới quan trọng nhất của Quy chế. Chốt Ca là bên cung cấp công cụ kết nối, **không phải bên tuyển dụng và không phải bên sử dụng lao động**:",
          items: [
            "**Tính chính xác của thông tin do người dùng tự khai**, gồm nội dung tin tuyển dụng và nội dung hồ sơ cá nhân. Chốt Ca kiểm duyệt và xử lý báo cáo, nhưng không thẩm định thực địa từng tin.",
            "**Quyết định tuyển dụng**. Chốt Ca không tham gia sàng lọc, phỏng vấn hay lựa chọn ứng viên thay Nhà tuyển dụng.",
            "**Nội dung và việc thực hiện hợp đồng lao động**, gồm lương, thưởng, thời giờ làm việc, bảo hiểm xã hội, kỷ luật lao động, chấm dứt hợp đồng và bồi thường tai nạn lao động.",
            "**Các khoản thanh toán giữa Nhà tuyển dụng và người lao động.** Chốt Ca không giữ, không thu hộ, không chi hộ và không bảo lãnh cho bất kỳ khoản nào.",
            "**Hành vi của các bên khi gặp nhau ngoài Nền tảng**, sau khi hai bên đã trao đổi thông tin liên hệ.",
            "**Việc Nhà tuyển dụng sử dụng dữ liệu ứng viên sau khi nhận được.** Từ thời điểm đó, Nhà tuyển dụng là Bên kiểm soát dữ liệu độc lập và tự chịu trách nhiệm trước pháp luật.",
          ],
        },
        {
          title: "3.3. Nhà tuyển dụng chịu trách nhiệm",
          items: [
            "Tính hợp pháp, chính xác và cập nhật của tin tuyển dụng và của cơ sở kinh doanh mình đại diện.",
            "Tuân thủ pháp luật lao động, gồm giao kết hợp đồng, mức lương tối thiểu vùng, bảo hiểm bắt buộc, an toàn vệ sinh lao động và các quy định về lao động chưa thành niên.",
            "**Không phân biệt đối xử trong tuyển dụng** theo Điều 8 Bộ luật Lao động 2019 và Điều 5 Luật Việc làm 2025.",
            "**Không thu bất kỳ khoản tiền nào từ Người tìm việc.**",
            "Bảo vệ dữ liệu ứng viên theo Điều 25 Luật Bảo vệ dữ liệu cá nhân 2025, gồm nghĩa vụ **xoá thông tin của người dự tuyển khi không tuyển dụng**.",
            "Chi tiết tại [Thoả thuận dành cho Nhà tuyển dụng](/employer-agreement).",
          ],
        },
        {
          title: "3.4. Người tìm việc chịu trách nhiệm",
          items: [
            "Tính trung thực của hồ sơ và việc chỉ sử dụng danh tính, giấy tờ của chính mình.",
            "Cân nhắc trước khi ứng tuyển, vì việc ứng tuyển đồng nghĩa với việc chia sẻ thông tin liên hệ cho Nhà tuyển dụng đó.",
            "Tự đánh giá tính phù hợp và độ tin cậy của Nhà tuyển dụng trước khi nhận việc, và tự bảo vệ theo hướng dẫn tại [Quy tắc dành cho Người tìm việc](/worker-conduct).",
            "Phản hồi lời mời phỏng vấn đúng hạn và giữ hẹn khi đã chốt lịch.",
          ],
        },
      ],
    },
    {
      title: "4. Quy trình đăng tin và kiểm duyệt",
      items: [
        "Nhà tuyển dụng tạo tin ở trạng thái nháp, điền vị trí công việc, loại hình, khoảng lương, loại lương, địa điểm và mô tả, rồi đăng tin.",
        "Hệ thống kiểm tra tiêu đề và mô tả để chặn nội dung vi phạm và việc chèn số điện thoại, Zalo hoặc đường dẫn ngoài nhằm kéo người dùng rời Nền tảng.",
        "Số tin được mở cùng lúc bị giới hạn theo gói thuê bao của Nhà tuyển dụng.",
        "Tin có thể bị **gỡ hoặc yêu cầu chỉnh sửa** khi vi phạm quy định, theo báo cáo của người dùng hoặc theo rà soát của quản trị viên.",
        "Tin kết thúc vòng đời khi được đóng, hết hạn hoặc bị huỷ.",
      ],
    },
    {
      title: "5. Quy trình kết nối và thời điểm chia sẻ thông tin liên hệ",
      sections: [
        {
          title: "5.1. Người tìm việc chủ động ứng tuyển",
          body: "Người tìm việc bấm ứng tuyển. **Ngay tại thời điểm đó**, Nhà tuyển dụng nhận toàn bộ hồ sơ kèm họ tên, số điện thoại và email. Nhà tuyển dụng có thể mời phỏng vấn mà **không mất phí**. Đây là luồng chính và chiếm đa số hoạt động trên Nền tảng.",
        },
        {
          title: "5.2. Nhà tuyển dụng chủ động tìm ứng viên",
          body: "Nhà tuyển dụng duyệt kho ứng viên gồm những người đã tự bật chế độ Đang tìm việc. Hồ sơ hiển thị ở **dạng che**. Khi gửi lời mời phỏng vấn, hệ thống trừ **01 lượt săn ứng viên**. Thông tin liên hệ **chỉ được mở khi Người tìm việc bấm nhận lời mời và chọn khung giờ**. Nếu người đó từ chối hoặc để lời mời hết hạn sau 24 giờ, lượt săn được **hoàn lại tự động** và thông tin liên hệ không bao giờ được mở.",
        },
        {
          title: "5.3. Chốt lịch và sau khi chốt lịch",
          body: "Sau khi Người tìm việc nhận lời mời, có cửa sổ **30 phút** để đổi ý. Hết 30 phút, lịch được chốt và hệ thống lưu bản sao thông tin liên hệ hai chiều vào lời mời. Từ thời điểm này, hai bên liên hệ trực tiếp với nhau. **Chốt Ca không có tính năng nhắn tin trong ứng dụng** và không tham gia vào các trao đổi sau đó.",
        },
      ],
    },
    {
      title: "6. Quy trình thanh toán",
      items: [
        "Chỉ Nhà tuyển dụng phát sinh giao dịch thanh toán. Người tìm việc không trả phí.",
        "Giá và hạn mức được hiển thị đầy đủ trước khi xác nhận. Giá đã bao gồm thuế giá trị gia tăng.",
        "Đơn hàng được tạo ở trạng thái chờ thanh toán, có nội dung chuyển khoản riêng và thời hạn thanh toán.",
        "Thanh toán qua **chuyển khoản ngân hàng bằng mã QR** thông qua đối tác Sepay. Giao dịch hoàn tất khi Chốt Ca nhận xác nhận từ Sepay.",
        "Gói thuê bao có thời hạn **30 ngày, không tự động gia hạn, không tự động trừ tiền**.",
        "Toàn bộ lịch sử đơn hàng và chứng từ được lưu trong tài khoản để Nhà tuyển dụng tra cứu, tải về và in.",
        "Chi tiết và chính sách hoàn tiền tại [Điều khoản thanh toán](/payment-terms).",
      ],
    },
    {
      title: "7. Quy trình tiếp nhận và giải quyết phản ánh, khiếu nại",
      items: [
        "**Bước 1 — Gửi yêu cầu.** Dùng nút Báo cáo ngay trên tin, hồ sơ hoặc doanh nghiệp, hoặc gửi email tới đầu mối tương ứng tại mục 1. Nêu rõ mã tin hoặc mã đơn hàng, mô tả sự việc và đính kèm bằng chứng.",
        "**Bước 2 — Xác nhận tiếp nhận.** Chúng tôi xác nhận đã nhận trong **24 giờ làm việc**, riêng yêu cầu về dữ liệu cá nhân xác nhận trong **72 giờ**.",
        "**Bước 3 — Xác minh.** Chúng tôi đối chiếu nhật ký hệ thống và có thể liên hệ các bên liên quan để làm rõ.",
        "**Bước 4 — Xử lý.** Tuỳ mức độ: yêu cầu chỉnh sửa, gỡ nội dung, cảnh cáo, hạn chế tính năng, tạm khoá hoặc chấm dứt tài khoản. Với giao dịch, xử lý gồm cấp bù quyền lợi, gia hạn hoặc hoàn tiền theo Điều khoản thanh toán mục 6.3.",
        "**Bước 5 — Thông báo kết quả.** Chúng tôi thông báo kết quả và nêu căn cứ. Thời hạn xử lý theo từng loại yêu cầu được công bố tại [Câu hỏi thường gặp](/cau-hoi-thuong-gap) mục 5.",
        "**Bước 6 — Khiếu nại lại.** Bên bị xử lý có **14 ngày** để yêu cầu xem xét lại. Quyết định chấm dứt tài khoản luôn được một người rà lại, không do hệ thống tự động quyết.",
        "**Bước 7 — Cơ quan có thẩm quyền.** Nếu không đạt được thoả thuận, các bên có quyền khiếu nại tới cơ quan quản lý nhà nước có thẩm quyền hoặc khởi kiện tại toà án theo quy định của pháp luật.",
      ],
    },
    {
      title: "8. Cảnh báo và xử lý tin đăng có dấu hiệu vi phạm",
      body: "Theo Điều 40 Luật Bảo vệ quyền lợi người tiêu dùng 2023, khi phát hiện tin tuyển dụng hoặc tài khoản có dấu hiệu lừa đảo hoặc vi phạm pháp luật, Chốt Ca gỡ nội dung, khoá tài khoản liên quan và có thể phát cảnh báo tới những người dùng đã tương tác với nội dung đó. Chúng tôi gỡ bỏ cảnh báo khi có căn cứ cho thấy dấu hiệu vi phạm không còn.",
    },
    {
      title: "9. Bảo vệ dữ liệu cá nhân",
      body: "Việc thu thập, sử dụng, chia sẻ, lưu trữ và xoá dữ liệu cá nhân trên Nền tảng thực hiện theo [Chính sách bảo vệ dữ liệu cá nhân](/privacy) và [Hướng dẫn xoá dữ liệu](/data-deletion), phù hợp với **Luật Bảo vệ dữ liệu cá nhân số 91/2025/QH15**. Nhà tuyển dụng nhận dữ liệu ứng viên trở thành Bên kiểm soát dữ liệu độc lập và có nghĩa vụ riêng theo Điều 25 của Luật này.",
    },
    {
      title: "10. Lưu trữ thông tin giao dịch",
      body: "Chốt Ca lưu trữ thông tin về gói dịch vụ, đơn hàng và giao dịch liên quan, cho phép Nhà tuyển dụng truy cập, truy vết, tải về và in hoá đơn, chứng từ trong khu vực quản trị tài khoản, theo Điều 39 khoản 3 điểm h Luật Bảo vệ quyền lợi người tiêu dùng 2023. Chứng từ kế toán được lưu **10 năm** theo Luật Kế toán 2015.",
    },
    {
      title: "11. Sửa đổi Quy chế",
      body: `Chốt Ca có thể sửa đổi Quy chế này khi sản phẩm hoặc pháp luật thay đổi. Bản mới được công bố tại ${C.websiteUrl}/platform-rules kèm số phiên bản và ngày hiệu lực. Với thay đổi làm ảnh hưởng tới quyền lợi của người dùng, chúng tôi thông báo trước **15 ngày** qua ứng dụng hoặc email.`,
    },
  ],
  contact: {
    title: "12. Liên hệ",
    prefix: "Phản ánh, khiếu nại liên quan tới Quy chế hoạt động: ",
    email: C.legalEmail,
  },
};
